import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  FaMapMarkerAlt,
  FaBuilding,
  FaArrowLeft,
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
  FaCheckCircle,
  FaClock,
  FaCalendarCheck,
  FaRegCircle,
  FaTrash,
  FaTimes,
  FaPen,
  FaSave,
  FaListUl,
  FaExclamationTriangle,
  FaCalendarPlus,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import {
  VISIT_TYPES,
  fetchVisitWindows,
  fetchVisitRequests,
  createVisitWindow,
  updateVisitWindow,
  deleteVisitWindow,
  updateVisitRequest,
  deleteVisitRequest,
  submitVisitRequest,
  computeOutstandingVisits,
  computeAvailabilityInsights,
  fetchVisitItinerary,
  saveVisitItinerary,
  normalizeVisitStatus,
  parseDateKey,
  toDateKey,
  addDays,
  formatWindowRange,
  todayKey,
} from '../../services/visitSchedulingService';
import PortalBackground from '../../components/PortalBackground';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Per-type accent colors used for ovals / scheduled state.
const ACCENT = {
  onsite: {
    bar: 'bg-emerald-500',
    barSoft: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200',
    ring: 'ring-emerald-500',
    btn: 'bg-emerald-600 hover:bg-emerald-700',
    tab: 'bg-emerald-500',
    tile: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
  },
  cqc: {
    bar: 'bg-red-500',
    barSoft: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200',
    ring: 'ring-red-500',
    btn: 'bg-red-600 hover:bg-red-700',
    tab: 'bg-red-500',
    tile: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
  },
};

function buildMonthCells(year, month) {
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const GeneralFulfillment = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('onsite'); // 'onsite' | 'cqc'
  const [loading, setLoading] = useState(true);

  const [windows, setWindows] = useState([]);
  const [requests, setRequests] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [outstandingLoading, setOutstandingLoading] = useState(false);

  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  // Modals
  const [windowModal, setWindowModal] = useState(null); // window object being managed
  const [showAddWindow, setShowAddWindow] = useState(false);
  const [addDay1, setAddDay1] = useState('');
  const [addDay2, setAddDay2] = useState('');
  const [addCapacity, setAddCapacity] = useState(2);

  // Manual assign
  const [assignAccountId, setAssignAccountId] = useState('');
  const [savingAssign, setSavingAssign] = useState(false);

  // Itinerary editor
  const [showItinerary, setShowItinerary] = useState(false);
  const [itinerary, setItinerary] = useState([]);
  const [itineraryDirty, setItineraryDirty] = useState(false);
  const [savingItinerary, setSavingItinerary] = useState(false);

  const accent = ACCENT[activeTab];
  const typeCfg = VISIT_TYPES[activeTab];

  // ---- Loaders ----
  const loadCore = useCallback(async (type) => {
    setLoading(true);
    const [w, r, itin] = await Promise.all([
      fetchVisitWindows(type),
      fetchVisitRequests(type),
      fetchVisitItinerary(type),
    ]);
    setWindows(w);
    setRequests(r);
    setItinerary(itin);
    setItineraryDirty(false);
    setLoading(false);
    // Outstanding is heavier — resolve after.
    setOutstandingLoading(true);
    const out = await computeOutstandingVisits(type, r);
    setOutstanding(out);
    setOutstandingLoading(false);
  }, []);

  useEffect(() => {
    loadCore(activeTab);
  }, [activeTab, loadCore]);

  // Active clients (for manual assign + labeling) — loaded once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'accounts'), where('type', '==', 'Clients')));
        if (cancelled) return;
        const list = snap.docs
          .map((d) => {
            const data = d.data();
            const contact = data.contact || {};
            const email = (contact.email || data.email || '').trim();
            const name =
              `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.name || '';
            return { id: d.id, company: data.company || '', email, name };
          })
          .filter((a) => a.email)
          .sort((a, b) => (a.company || a.email).localeCompare(b.company || b.email));
        setAccounts(list);
      } catch (e) {
        console.error('load accounts error:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const accountByEmail = useMemo(() => {
    const m = new Map();
    accounts.forEach((a) => m.set(a.email.toLowerCase(), a));
    return m;
  }, [accounts]);

  // Decorate a request with company / contact for display.
  const labelRequest = useCallback(
    (r) => {
      const email = String(r.userEmail || '').toLowerCase();
      const acc = accountByEmail.get(email);
      return {
        ...r,
        status: normalizeVisitStatus(r.status),
        company: r.companyName || acc?.company || r.userEmail || 'Company',
        contactName: r.requestedByName || acc?.name || '',
      };
    },
    [accountByEmail]
  );

  const decoratedRequests = useMemo(() => requests.map(labelRequest), [requests, labelRequest]);

  const requestsByWindow = useMemo(() => {
    const m = new Map();
    decoratedRequests.forEach((r) => {
      if (!r.windowId) return;
      if (!m.has(r.windowId)) m.set(r.windowId, []);
      m.get(r.windowId).push(r);
    });
    return m;
  }, [decoratedRequests]);

  // ---- Stats ----
  const stats = useMemo(() => {
    let completed = 0;
    let requested = 0;
    let scheduled = 0;
    decoratedRequests.forEach((r) => {
      if (r.status === 'completed') completed += 1;
      else if (r.status === 'scheduled') scheduled += 1;
      else if (r.status === 'requested') requested += 1;
    });
    return { completed, requested, scheduled, outstanding: outstanding.length };
  }, [decoratedRequests, outstanding]);

  const pendingRequests = useMemo(
    () => decoratedRequests.filter((r) => r.status === 'requested'),
    [decoratedRequests]
  );

  const insights = useMemo(
    () => computeAvailabilityInsights(windows, decoratedRequests),
    [windows, decoratedRequests]
  );

  const jumpToWindowMonth = (dayKey) => {
    const d = parseDateKey(dayKey);
    if (!d) return;
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  // ---- Window actions ----
  const refreshRequests = useCallback(async () => {
    const r = await fetchVisitRequests(activeTab);
    setRequests(r);
    return r.map(labelRequest);
  }, [activeTab, labelRequest]);

  const handleCreateWindow = async () => {
    if (!addDay1) {
      alert('Pick the first day of the visit.');
      return;
    }
    const d2 = addDay2 || addDays(addDay1, 1);
    if (d2 < addDay1) {
      alert('The second day must be on or after the first day.');
      return;
    }
    await createVisitWindow(activeTab, addDay1, d2, addCapacity);
    setShowAddWindow(false);
    setAddDay1('');
    setAddDay2('');
    setAddCapacity(2);
    setWindows(await fetchVisitWindows(activeTab));
  };

  const handleDeleteWindow = async (win) => {
    const assigned = requestsByWindow.get(win.id) || [];
    const active = assigned.filter((r) => r.status !== 'denied');
    if (active.length) {
      if (!window.confirm(`This window has ${active.length} company(ies) attached. Delete anyway? Their requests will be unscheduled.`)) {
        return;
      }
      // Unschedule attached requests back to requested with no window.
      await Promise.all(
        active.map((r) => updateVisitRequest(activeTab, r.id, { windowId: '', status: 'requested' }))
      );
    }
    await deleteVisitWindow(win.id);
    setWindowModal(null);
    setWindows(await fetchVisitWindows(activeTab));
    await refreshRequests();
  };

  const handleSaveWindowDates = async (win, day1, day2) => {
    await updateVisitWindow(win.id, { day1, day2: day2 || addDays(day1, 1), capacity: win.capacity });
    const fresh = await fetchVisitWindows(activeTab);
    setWindows(fresh);
    setWindowModal(fresh.find((w) => w.id === win.id) || null);
    // Keep attached requests' dates in sync.
    const attached = (requestsByWindow.get(win.id) || []).filter((r) => r.status !== 'denied');
    await Promise.all(attached.map((r) => updateVisitRequest(activeTab, r.id, { day1, day2: day2 || addDays(day1, 1) })));
    await refreshRequests();
  };

  const handleRequestAction = async (req, action, win) => {
    if (action === 'approve') {
      await updateVisitRequest(activeTab, req.id, {
        status: 'scheduled',
        windowId: req.windowId || win?.id || '',
        day1: req.day1 || win?.day1 || '',
        day2: req.day2 || win?.day2 || '',
      });
    } else if (action === 'deny') {
      await updateVisitRequest(activeTab, req.id, { status: 'denied' });
    } else if (action === 'complete') {
      await updateVisitRequest(activeTab, req.id, { status: 'completed' });
    } else if (action === 'unschedule') {
      await updateVisitRequest(activeTab, req.id, { status: 'requested', windowId: '' });
    } else if (action === 'delete') {
      if (!window.confirm('Delete this request entirely?')) return;
      await deleteVisitRequest(activeTab, req.id);
    }
    const fresh = await refreshRequests();
    // Refresh outstanding count if a completion changed.
    if (action === 'complete' || action === 'delete') {
      setOutstandingLoading(true);
      setOutstanding(await computeOutstandingVisits(activeTab, fresh.map((r) => ({ ...r }))));
      setOutstandingLoading(false);
    }
  };

  const handleManualAssign = async (win) => {
    if (!assignAccountId) return;
    const acc = accounts.find((a) => a.id === assignAccountId);
    if (!acc?.email) {
      alert('That client has no email on file.');
      return;
    }
    setSavingAssign(true);
    try {
      // Find the portal user uid by email so the client sees it on their side.
      let uid = '';
      try {
        let usersSnap = await getDocs(query(collection(db, 'users'), where('email', '==', acc.email)));
        if (usersSnap.empty) {
          usersSnap = await getDocs(
            query(collection(db, 'users'), where('email', '==', acc.email.toLowerCase()))
          );
        }
        if (!usersSnap.empty) uid = usersSnap.docs[0].id;
      } catch {
        /* ignore */
      }
      const docId = uid || `manual_${acc.id}`;
      await submitVisitRequest(activeTab, docId, {
        userId: uid,
        userEmail: acc.email,
        companyName: acc.company,
        requestedByName: acc.name,
        windowId: win.id,
        day1: win.day1,
        day2: win.day2,
        status: 'scheduled',
        manualEntry: true,
      });
      setAssignAccountId('');
      await refreshRequests();
    } catch (e) {
      console.error('manual assign error:', e);
      alert('Could not assign the client. Check permissions and try again.');
    } finally {
      setSavingAssign(false);
    }
  };

  // ---- Itinerary editing ----
  const updateBlock = (dayIdx, blockIdx, field, value) => {
    setItinerary((prev) => {
      const next = prev.map((d) => ({ ...d, blocks: [...(d.blocks || [])] }));
      next[dayIdx].blocks[blockIdx] = { ...next[dayIdx].blocks[blockIdx], [field]: value };
      return next;
    });
    setItineraryDirty(true);
  };
  const updateDayTitle = (dayIdx, value) => {
    setItinerary((prev) => prev.map((d, i) => (i === dayIdx ? { ...d, title: value } : d)));
    setItineraryDirty(true);
  };
  const addBlock = (dayIdx) => {
    setItinerary((prev) => {
      const next = prev.map((d) => ({ ...d, blocks: [...(d.blocks || [])] }));
      next[dayIdx].blocks.push({ time: '', activity: '' });
      return next;
    });
    setItineraryDirty(true);
  };
  const removeBlock = (dayIdx, blockIdx) => {
    setItinerary((prev) => {
      const next = prev.map((d) => ({ ...d, blocks: [...(d.blocks || [])] }));
      next[dayIdx].blocks.splice(blockIdx, 1);
      return next;
    });
    setItineraryDirty(true);
  };
  const handleSaveItinerary = async () => {
    setSavingItinerary(true);
    try {
      await saveVisitItinerary(activeTab, itinerary);
      setItineraryDirty(false);
    } catch (e) {
      console.error('save itinerary error:', e);
      alert('Could not save itinerary.');
    } finally {
      setSavingItinerary(false);
    }
  };

  // ---- Calendar derived ----
  const cells = useMemo(() => buildMonthCells(viewYear, viewMonth), [viewYear, viewMonth]);
  const windowsByDay = useMemo(() => {
    const m = new Map();
    windows.forEach((w) => {
      [w.day1, w.day2].forEach((dk, i) => {
        if (!dk) return;
        if (!m.has(dk)) m.set(dk, []);
        m.get(dk).push({ win: w, isStart: i === 0, isEnd: i === 1 });
      });
    });
    return m;
  }, [windows]);

  const windowState = useCallback(
    (win) => {
      const reqs = (requestsByWindow.get(win.id) || []).filter((r) => r.status !== 'denied');
      const hasScheduled = reqs.some((r) => r.status === 'scheduled' || r.status === 'completed');
      const hasRequested = reqs.some((r) => r.status === 'requested');
      const cap = Number(win.capacity) || 2;
      const used = reqs.filter((r) => r.status !== 'requested').length;
      return { reqs, hasScheduled, hasRequested, cap, used };
    },
    [requestsByWindow]
  );

  const goPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  };
  const goNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  };

  const cardCls = `rounded-xl border shadow-sm ${
    darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
  }`;

  // ---- Render ----
  const statCards = [
    { key: 'completed', label: 'Completed', value: stats.completed, icon: FaCheckCircle, tile: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' },
    { key: 'requested', label: 'Requested', value: stats.requested, icon: FaClock, tile: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' },
    { key: 'scheduled', label: 'Scheduled', value: stats.scheduled, icon: FaCalendarCheck, tile: 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400' },
    { key: 'outstanding', label: 'Outstanding', value: outstandingLoading ? '…' : stats.outstanding, icon: FaRegCircle, tile: 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400' },
  ];

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="relative flex items-center justify-center">
            <button
              onClick={() => navigate('/crm/fulfillment')}
              className={`absolute left-0 flex h-10 w-10 items-center justify-center rounded-md transition-colors ${
                darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Back to Fulfillment"
            >
              <FaArrowLeft className="h-5 w-5" />
            </button>
            <div className="text-center">
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">General Fulfillment</h1>
              <p className="mt-1 text-gray-600 dark:text-gray-300">
                Schedule and manage 2-day {typeCfg.label} appointments
              </p>
            </div>
            <button
              onClick={() => {
                setAddDay1('');
                setAddDay2('');
                setAddCapacity(2);
                setShowAddWindow(true);
              }}
              className={`absolute right-0 flex h-10 items-center gap-2 rounded-md px-4 text-sm font-medium text-white transition-colors ${accent.btn}`}
              title="Add available window"
            >
              <FaPlus className="h-4 w-4" />
              <span className="hidden sm:inline">Add window</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex justify-center">
          <div className={`inline-flex rounded-xl p-1 shadow-sm border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900`}>
            <button
              onClick={() => setActiveTab('onsite')}
              className={`flex items-center rounded-md px-6 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'onsite'
                  ? `${ACCENT.onsite.tab} text-white`
                  : darkMode
                    ? 'text-gray-300 hover:bg-gray-700'
                    : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FaMapMarkerAlt className="mr-2" /> On-Site Visits
            </button>
            <button
              onClick={() => setActiveTab('cqc')}
              className={`flex items-center rounded-md px-6 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'cqc'
                  ? `${ACCENT.cqc.tab} text-white`
                  : darkMode
                    ? 'text-gray-300 hover:bg-gray-700'
                    : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FaBuilding className="mr-2" /> CQC Visits
            </button>
          </div>
        </div>

        {/* Coverage health banner */}
        {(() => {
          const severity = insights.noUpcoming || insights.noOpen ? 'danger' : insights.lowCoverage ? 'warn' : 'ok';
          const styles = {
            danger: {
              box: 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20',
              icon: 'text-red-600 dark:text-red-400',
              Icon: FaExclamationTriangle,
            },
            warn: {
              box: 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20',
              icon: 'text-amber-600 dark:text-amber-400',
              Icon: FaExclamationTriangle,
            },
            ok: {
              box: darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white',
              icon: 'text-emerald-600 dark:text-emerald-400',
              Icon: FaCheckCircle,
            },
          }[severity];
          const message =
            insights.noUpcoming
              ? `No upcoming ${typeCfg.label} windows at all — add dates so clients can book.`
              : insights.noOpen
                ? `Every upcoming ${typeCfg.label} window is fully booked — add more dates.`
                : insights.lowCoverage
                  ? `No open seats in the next ${insights.lowCoverageDays} days — consider adding nearer-term dates.`
                  : `Coverage looks healthy. Next open date ${insights.nextOpenDate ? formatWindowRange(insights.nextOpenDate, null) : '—'}.`;
          return (
            <div className={`mb-6 flex flex-col gap-3 rounded-xl border p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between ${styles.box}`}>
              <div className="flex items-start gap-3">
                <styles.Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${styles.icon}`} />
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">{message}</div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
                    <span><strong>{insights.openWindowCount}</strong> open window{insights.openWindowCount === 1 ? '' : 's'}</span>
                    <span><strong>{insights.totalOpenSeats}</strong> open seat{insights.totalOpenSeats === 1 ? '' : 's'}</span>
                    <span><strong>{insights.emptyWindowCount}</strong> with no registrations</span>
                    {insights.coverageThrough && (
                      <span>covered through <strong>{formatWindowRange(insights.coverageThrough, null)}</strong></span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setAddDay1('');
                  setAddDay2('');
                  setAddCapacity(2);
                  setShowAddWindow(true);
                }}
                className={`flex flex-shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white ${accent.btn}`}
              >
                <FaCalendarPlus className="h-4 w-4" /> Add dates
              </button>
            </div>
          );
        })()}

        {/* Stat cards */}
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {statCards.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.key} className={`${cardCls} flex items-center gap-4 p-5`}>
                <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${s.tile}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{s.value}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{s.label}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Calendar */}
          <div className={`${cardCls} p-5 lg:col-span-2`}>
            <div className="mb-4 flex items-center justify-between">
              <button
                onClick={goPrevMonth}
                className={`flex h-9 w-9 items-center justify-center rounded-md ${
                  darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <FaChevronLeft />
              </button>
              <h2 className="text-center text-lg font-semibold text-gray-900 dark:text-white">
                {MONTHS[viewMonth]} {viewYear}
              </h2>
              <button
                onClick={goNextMonth}
                className={`flex h-9 w-9 items-center justify-center rounded-md ${
                  darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <FaChevronRight />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-px">
              {DOW.map((d) => (
                <div key={d} className="pb-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {d}
                </div>
              ))}
              {cells.map((cell, idx) => {
                if (!cell) return <div key={`b-${idx}`} className="min-h-[88px]" />;
                const dk = toDateKey(cell);
                const isToday = dk === todayKey();
                const entries = windowsByDay.get(dk) || [];
                return (
                  <div
                    key={dk}
                    className={`min-h-[88px] rounded-md border p-1 ${
                      darkMode ? 'border-gray-800' : 'border-gray-100'
                    } ${isToday ? (darkMode ? 'bg-gray-800/60' : 'bg-gray-50') : ''}`}
                  >
                    <div className={`mb-1 text-right text-xs ${isToday ? 'font-bold text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                      {cell.getDate()}
                    </div>
                    <div className="space-y-1">
                      {entries.map(({ win, isStart, isEnd }) => {
                        const st = windowState(win);
                        const barColor = st.hasScheduled
                          ? `${accent.bar} text-white`
                          : st.hasRequested
                            ? 'bg-amber-400 text-amber-950'
                            : darkMode
                              ? 'bg-gray-700 text-gray-200'
                              : 'bg-gray-200 text-gray-600';
                        return (
                          <button
                            key={win.id}
                            onClick={() => {
                              setWindowModal(win);
                              setAssignAccountId('');
                            }}
                            title={`${formatWindowRange(win.day1, win.day2)} · ${st.used}/${st.cap} booked`}
                            className={`block w-full truncate px-1.5 py-1 text-left text-[11px] font-medium ${barColor} ${
                              isStart ? 'rounded-l-full ' : ''
                            }${isEnd ? 'rounded-r-full' : ''}`}
                          >
                            {isStart ? `${st.used}/${st.cap} · ${st.reqs.filter((r) => r.status !== 'requested')[0]?.company || 'Open'}` : '→'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1.5"><span className={`inline-block h-3 w-5 rounded-full ${accent.bar}`} /> Scheduled</span>
              <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-5 rounded-full bg-amber-400" /> Requested</span>
              <span className="flex items-center gap-1.5"><span className={`inline-block h-3 w-5 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} /> Open</span>
            </div>
          </div>

          {/* Side column: pending requests + outstanding */}
          <div className="space-y-6">
            <div className={`${cardCls} p-5`}>
              <h3 className="mb-3 flex items-center gap-2 text-center text-base font-semibold text-gray-900 dark:text-white justify-center">
                <FaClock className="text-amber-500" /> Pending requests ({pendingRequests.length})
              </h3>
              {pendingRequests.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">No requests waiting for approval.</p>
              ) : (
                <ul className="space-y-3">
                  {pendingRequests.map((r) => {
                    const win = windows.find((w) => w.id === r.windowId);
                    return (
                      <li key={r.id} className={`rounded-xl border p-3 ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                        <div className="font-medium text-gray-900 dark:text-white">{r.company}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{r.contactName || r.userEmail}</div>
                        <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                          {win || r.day1 ? formatWindowRange(r.day1 || win?.day1, r.day2 || win?.day2) : 'No date selected'}
                        </div>
                        <div className="mt-2 flex gap-2">
                          <button
                            onClick={() => handleRequestAction(r, 'approve', win)}
                            className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium text-white ${accent.btn}`}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleRequestAction(r, 'deny')}
                            className="flex-1 rounded-md border border-red-300 px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/30"
                          >
                            Deny
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className={`${cardCls} p-5`}>
              <h3 className="mb-3 flex items-center justify-center gap-2 text-center text-base font-semibold text-gray-900 dark:text-white">
                <FaRegCircle className="text-violet-500" /> Outstanding ({outstandingLoading ? '…' : outstanding.length})
              </h3>
              <p className="mb-3 text-center text-xs text-gray-500 dark:text-gray-400">
                Active clients with an unused {typeCfg.label} deliverable.
              </p>
              {outstandingLoading ? (
                <p className="py-4 text-center text-sm text-gray-400">Loading…</p>
              ) : outstanding.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">None outstanding.</p>
              ) : (
                <ul className="max-h-64 space-y-2 overflow-y-auto">
                  {outstanding.map((o) => (
                    <li key={o.accountId} className={`rounded-xl border px-3 py-2 ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{o.company}</div>
                      {o.contactName && <div className="text-xs text-gray-500 dark:text-gray-400">{o.contactName}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={`${cardCls} p-5`}>
              <h3 className="mb-3 flex items-center justify-center gap-2 text-center text-base font-semibold text-gray-900 dark:text-white">
                <FaCalendarPlus className="text-sky-500" /> Open windows, no registrations ({insights.emptyWindowCount})
              </h3>
              <p className="mb-3 text-center text-xs text-gray-500 dark:text-gray-400">
                Upcoming dates nobody has booked yet — promote or fill them.
              </p>
              {insights.emptyWindows.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  {insights.noUpcoming ? 'No upcoming windows — add some.' : 'Every upcoming window has a registration.'}
                </p>
              ) : (
                <ul className="max-h-64 space-y-2 overflow-y-auto">
                  {insights.emptyWindows.map((w) => (
                    <li key={w.id}>
                      <button
                        onClick={() => {
                          jumpToWindowMonth(w.day1);
                          setWindowModal(w);
                          setAssignAccountId('');
                        }}
                        className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left transition-colors ${
                          darkMode ? 'border-gray-800 hover:bg-gray-800' : 'border-gray-100 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatWindowRange(w.day1, w.day2)}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{w.seatsLeft} open</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Itinerary editor */}
        <div className={`${cardCls} mt-6 p-5`}>
          <button
            onClick={() => setShowItinerary((v) => !v)}
            className="flex w-full items-center justify-center gap-2 text-center text-base font-semibold text-gray-900 dark:text-white"
          >
            <FaListUl className={accent.tile.split(' ').find((c) => c.startsWith('text-')) || ''} />
            Client-facing itinerary — {typeCfg.label}
            <span className="text-xs font-normal text-gray-400">({showItinerary ? 'hide' : 'edit'})</span>
          </button>

          {showItinerary && (
            <div className="mt-5 space-y-6">
              {itinerary.map((day, dayIdx) => (
                <div key={dayIdx} className={`rounded-xl border p-4 ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
                  <div className="mb-3 flex items-center gap-3">
                    <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${accent.bar}`}>
                      {day.day || dayIdx + 1}
                    </span>
                    <input
                      value={day.title || ''}
                      onChange={(e) => updateDayTitle(dayIdx, e.target.value)}
                      placeholder="Day title"
                      className={`flex-1 rounded-md border px-3 py-1.5 text-sm font-semibold ${
                        darkMode ? 'border-gray-700 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'
                      }`}
                    />
                  </div>
                  <div className="space-y-2">
                    {(day.blocks || []).map((b, blockIdx) => (
                      <div key={blockIdx} className="flex items-center gap-2">
                        <input
                          value={b.time || ''}
                          onChange={(e) => updateBlock(dayIdx, blockIdx, 'time', e.target.value)}
                          placeholder="9:00 AM"
                          className={`w-28 flex-shrink-0 rounded-md border px-2 py-1.5 text-sm ${
                            darkMode ? 'border-gray-700 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                        <input
                          value={b.activity || ''}
                          onChange={(e) => updateBlock(dayIdx, blockIdx, 'activity', e.target.value)}
                          placeholder="Activity"
                          className={`flex-1 rounded-md border px-2 py-1.5 text-sm ${
                            darkMode ? 'border-gray-700 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'
                          }`}
                        />
                        <button
                          onClick={() => removeBlock(dayIdx, blockIdx)}
                          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
                        >
                          <FaTrash className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => addBlock(dayIdx)}
                      className={`mt-1 flex items-center gap-1.5 text-sm font-medium ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                      <FaPlus className="h-3 w-3" /> Add time block
                    </button>
                  </div>
                </div>
              ))}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveItinerary}
                  disabled={!itineraryDirty || savingItinerary}
                  className={`flex items-center gap-2 rounded-md px-5 py-2 text-sm font-medium text-white ${accent.btn} disabled:opacity-50`}
                >
                  <FaSave className="h-4 w-4" />
                  {savingItinerary ? 'Saving…' : itineraryDirty ? 'Save itinerary' : 'Saved'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add window modal */}
      {showAddWindow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className={`w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl bg-white dark:bg-gray-900`}>
            <div className="relative border-b border-gray-200 px-6 py-4 dark:border-gray-800">
              <div className="text-center">
                <div className={`mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full ${accent.tile}`}>
                  <FaPlus className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add available window</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">2-day {typeCfg.label} slot</p>
              </div>
              <button onClick={() => setShowAddWindow(false)} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600">
                <FaTimes />
              </button>
            </div>
            <div className="space-y-4 px-6 py-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`mb-1 block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Day 1</label>
                  <input
                    type="date"
                    value={addDay1}
                    onChange={(e) => {
                      setAddDay1(e.target.value);
                      if (!addDay2 || addDay2 < e.target.value) setAddDay2(addDays(e.target.value, 1));
                    }}
                    className={`w-full rounded-md border px-3 py-2 text-sm ${darkMode ? 'border-gray-700 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                  />
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Day 2</label>
                  <input
                    type="date"
                    value={addDay2}
                    min={addDay1}
                    onChange={(e) => setAddDay2(e.target.value)}
                    className={`w-full rounded-md border px-3 py-2 text-sm ${darkMode ? 'border-gray-700 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                  />
                </div>
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Capacity (companies that can share these days)
                </label>
                <input
                  type="number"
                  min={1}
                  value={addCapacity}
                  onChange={(e) => setAddCapacity(Math.max(1, Number(e.target.value) || 1))}
                  className={`w-full rounded-md border px-3 py-2 text-sm ${darkMode ? 'border-gray-700 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-800">
              <button onClick={() => setShowAddWindow(false)} className={`rounded-md px-4 py-2 text-sm ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>
                Cancel
              </button>
              <button onClick={handleCreateWindow} className={`rounded-md px-4 py-2 text-sm font-medium text-white ${accent.btn}`}>
                Add window
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage window modal */}
      {windowModal && (
        <WindowModal
          win={windowModal}
          type={activeTab}
          accent={accent}
          darkMode={darkMode}
          reqs={(requestsByWindow.get(windowModal.id) || [])}
          state={windowState(windowModal)}
          accounts={accounts}
          assignAccountId={assignAccountId}
          setAssignAccountId={setAssignAccountId}
          savingAssign={savingAssign}
          onClose={() => setWindowModal(null)}
          onRequestAction={handleRequestAction}
          onManualAssign={handleManualAssign}
          onSaveDates={handleSaveWindowDates}
          onDelete={handleDeleteWindow}
        />
      )}

      {loading && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-4 py-2 text-sm text-white">
          Loading {typeCfg.label} data…
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Window management modal
// ---------------------------------------------------------------------------
function WindowModal({
  win,
  type,
  accent,
  darkMode,
  reqs,
  state,
  accounts,
  assignAccountId,
  setAssignAccountId,
  savingAssign,
  onClose,
  onRequestAction,
  onManualAssign,
  onSaveDates,
  onDelete,
}) {
  const [editing, setEditing] = useState(false);
  const [d1, setD1] = useState(win.day1);
  const [d2, setD2] = useState(win.day2);
  const typeCfg = VISIT_TYPES[type];

  useEffect(() => {
    setD1(win.day1);
    setD2(win.day2);
  }, [win.day1, win.day2]);

  const active = reqs.filter((r) => r.status !== 'denied');
  const statusPill = (status) => {
    const map = {
      requested: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
      scheduled: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
      completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
      denied: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    };
    return map[status] || map.requested;
  };

  // Active clients not already on this window.
  const assignedEmails = new Set(active.map((r) => String(r.userEmail || '').toLowerCase()));
  const assignable = accounts.filter((a) => !assignedEmails.has(a.email.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className={`max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl bg-white dark:bg-gray-900`}>
        <div className="relative border-b border-gray-200 px-6 py-4 dark:border-gray-800">
          <div className="text-center">
            <div className={`mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full ${accent.tile}`}>
              <FaCalendarCheck className="h-5 w-5" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{typeCfg.label} window</h3>
            {!editing ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formatWindowRange(win.day1, win.day2)} · {state.used}/{state.cap} booked
              </p>
            ) : (
              <div className="mt-2 flex items-center justify-center gap-2">
                <input
                  type="date"
                  value={d1}
                  onChange={(e) => {
                    setD1(e.target.value);
                    if (!d2 || d2 < e.target.value) setD2(addDays(e.target.value, 1));
                  }}
                  className={`rounded-md border px-2 py-1 text-sm ${darkMode ? 'border-gray-700 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                />
                <span className="text-gray-400">→</span>
                <input
                  type="date"
                  value={d2}
                  min={d1}
                  onChange={(e) => setD2(e.target.value)}
                  className={`rounded-md border px-2 py-1 text-sm ${darkMode ? 'border-gray-700 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                />
              </div>
            )}
          </div>
          <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600">
            <FaTimes />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          {/* Attached companies */}
          <div>
            <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              Companies on these days
            </h4>
            {active.length === 0 ? (
              <p className="rounded-lg border border-dashed p-4 text-center text-sm text-gray-400 dark:border-gray-700">
                No companies assigned yet.
              </p>
            ) : (
              <ul className="space-y-2">
                {active.map((r) => (
                  <li key={r.id} className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border p-3 ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{r.company}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{r.contactName || r.userEmail}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusPill(r.status)}`}>{r.status}</span>
                      {r.status === 'requested' && (
                        <button onClick={() => onRequestAction(r, 'approve', win)} className={`rounded-md px-2 py-1 text-xs font-medium text-white ${accent.btn}`}>
                          Approve
                        </button>
                      )}
                      {r.status === 'scheduled' && (
                        <button onClick={() => onRequestAction(r, 'complete')} className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700">
                          Mark done
                        </button>
                      )}
                      {(r.status === 'scheduled' || r.status === 'completed') && (
                        <button onClick={() => onRequestAction(r, 'unschedule')} className="rounded-md border px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                          Unschedule
                        </button>
                      )}
                      <button onClick={() => onRequestAction(r, 'delete')} className="text-red-500 hover:text-red-600">
                        <FaTrash className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Manual assign */}
          {state.used < state.cap && (
            <div>
              <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Manually assign a company</h4>
              <div className="flex gap-2">
                <select
                  value={assignAccountId}
                  onChange={(e) => setAssignAccountId(e.target.value)}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm ${darkMode ? 'border-gray-700 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                >
                  <option value="">Select an active client…</option>
                  {assignable.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.company || a.email}{a.name ? ` — ${a.name}` : ''}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => onManualAssign(win)}
                  disabled={!assignAccountId || savingAssign}
                  className={`rounded-md px-4 py-2 text-sm font-medium text-white ${accent.btn} disabled:opacity-50`}
                >
                  {savingAssign ? 'Adding…' : 'Assign'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex flex-wrap justify-between gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-800">
          <button
            onClick={() => onDelete(win)}
            className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            <FaTrash className="h-4 w-4" /> Delete window
          </button>
          <div className="flex gap-3">
            {editing ? (
              <button
                onClick={async () => {
                  await onSaveDates(win, d1, d2);
                  setEditing(false);
                }}
                className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white ${accent.btn}`}
              >
                <FaSave className="h-4 w-4" /> Save dates
              </button>
            ) : (
              <button
                onClick={() => setEditing(true)}
                className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              >
                <FaPen className="h-4 w-4" /> Edit dates
              </button>
            )}
            <button onClick={onClose} className={`rounded-md px-4 py-2 text-sm ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GeneralFulfillment;
